import { useState, useEffect } from 'react';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Package, User, MapPin, Phone, Mail, Clock, CheckCircle, Truck, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
  pending:   { color: '#F39C12', bg: '#FEF9E7', icon: Clock },
  confirmed: { color: '#2980B9', bg: '#EBF5FB', icon: CheckCircle },
  shipped:   { color: '#8E44AD', bg: '#F5EEF8', icon: Truck },
  delivered: { color: '#27AE60', bg: '#EAFAF1', icon: CheckCircle },
  cancelled: { color: '#C0392B', bg: '#FDEDEC', icon: XCircle },
};

export default function UserDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [profile, setProfile] = useState({ full_name: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    if (user) setProfile({ full_name: user.full_name, phone: user.phone || '', address: user.address || '' });
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await API.get('/orders/my');
      setOrders(res.data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Update local storage with new name
      const updatedUser = { ...user, ...profile };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (tab) => ({
    padding: '0.6rem 1.5rem',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid var(--charcoal)' : '3px solid transparent',
    background: 'transparent',
    fontWeight: activeTab === tab ? '700' : '500',
    fontSize: '0.95rem',
    color: activeTab === tab ? 'var(--charcoal)' : 'var(--mid-gray)',
    cursor: 'pointer',
    transition: 'var(--transition)'
  });

  return (
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 70px)', padding: '2rem' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          background: 'var(--charcoal)', borderRadius: 'var(--radius)',
          padding: '2rem', marginBottom: '2rem',
          display: 'flex', alignItems: 'center', gap: '1.5rem'
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'var(--soft-gold)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.6rem', fontWeight: '700', color: 'var(--charcoal)',
            fontFamily: 'var(--font-display)', flexShrink: 0
          }}>
            {user?.full_name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.6rem', color: 'var(--white)', marginBottom: '0.2rem'
            }}>{user?.full_name}</h1>
            <p style={{ color: 'var(--mid-gray)', fontSize: '0.9rem' }}>{user?.email}</p>
            <span style={{
              display: 'inline-block', marginTop: '0.4rem',
              background: 'var(--soft-gold)', color: 'var(--charcoal)',
              padding: '0.15rem 0.7rem', borderRadius: '20px',
              fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase'
            }}>Customer</span>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <p style={{ color: 'var(--soft-gold)', fontSize: '2rem', fontWeight: '700', fontFamily: 'var(--font-display)' }}>
              {orders.length}
            </p>
            <p style={{ color: 'var(--mid-gray)', fontSize: '0.85rem' }}>Total Orders</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-sm)', overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex', borderBottom: '1px solid var(--light-gray)',
            padding: '0 1rem'
          }}>
            <button style={tabStyle('orders')} onClick={() => setActiveTab('orders')}>
              <Package size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              My Orders
            </button>
            <button style={tabStyle('profile')} onClick={() => setActiveTab('profile')}>
              <User size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Profile
            </button>
          </div>

          <div style={{ padding: '2rem' }}>

            {/* ORDERS TAB */}
            {activeTab === 'orders' && (
              <div>
                {loading ? (
                  <p style={{ color: 'var(--mid-gray)', textAlign: 'center', padding: '2rem' }}>Loading orders...</p>
                ) : orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid-gray)' }}>
                    <Package size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                    <p style={{ fontSize: '1rem' }}>No orders yet</p>
                    <p style={{ fontSize: '0.85rem', marginTop: '0.3rem' }}>Start shopping to see your orders here</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {orders.map(order => {
                      const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                      const StatusIcon = cfg.icon;
                      const isExpanded = expandedOrder === order.id;
                      return (
                        <div key={order.id} style={{
                          border: '1.5px solid var(--light-gray)',
                          borderRadius: 'var(--radius)', overflow: 'hidden'
                        }}>
                          {/* Order Header */}
                          <div
                            onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                            style={{
                              padding: '1rem 1.2rem',
                              display: 'flex', alignItems: 'center',
                              gap: '1rem', cursor: 'pointer',
                              background: isExpanded ? 'var(--light-gray)' : 'var(--white)',
                              transition: 'var(--transition)'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.3rem' }}>
                                <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>Order #{order.id}</span>
                                <span style={{
                                  background: cfg.bg, color: cfg.color,
                                  padding: '0.2rem 0.7rem', borderRadius: '20px',
                                  fontSize: '0.75rem', fontWeight: '600',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  textTransform: 'capitalize'
                                }}>
                                  <StatusIcon size={12} /> {order.status}
                                </span>
                              </div>
                              <p style={{ fontSize: '0.82rem', color: 'var(--mid-gray)' }}>
                                {new Date(order.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric', month: 'long', day: 'numeric'
                                })}
                                {' · '}{order.items?.length} item{order.items?.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ fontWeight: '700', fontSize: '1.1rem', color: 'var(--warm-brown)' }}>
                                ${parseFloat(order.total_amount).toFixed(2)}
                              </p>
                              <p style={{ fontSize: '0.78rem', color: 'var(--mid-gray)' }}>
                                {isExpanded ? '▲ Hide' : '▼ Details'}
                              </p>
                            </div>
                          </div>

                          {/* Expanded Order Items */}
                          {isExpanded && (
                            <div style={{ padding: '1rem 1.2rem', borderTop: '1px solid var(--light-gray)', background: '#FDFCFA' }}>
                              {order.shipping_address && (
                                <div style={{
                                  display: 'flex', alignItems: 'flex-start', gap: '6px',
                                  marginBottom: '1rem', padding: '0.7rem',
                                  background: 'var(--light-gray)', borderRadius: '8px'
                                }}>
                                  <MapPin size={14} style={{ color: 'var(--warm-brown)', marginTop: '2px', flexShrink: 0 }} />
                                  <span style={{ fontSize: '0.85rem', color: 'var(--charcoal)' }}>{order.shipping_address}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                                {order.items?.map(item => (
                                  <div key={item.id} style={{
                                    display: 'flex', alignItems: 'center',
                                    gap: '1rem', padding: '0.6rem',
                                    background: 'var(--white)', borderRadius: '8px',
                                    border: '1px solid var(--light-gray)'
                                  }}>
                                    <img
                                      src={item.product?.image_url || 'https://via.placeholder.com/60'}
                                      alt={item.product?.name}
                                      style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '8px' }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <p style={{ fontWeight: '600', fontSize: '0.9rem' }}>{item.product?.name}</p>
                                      <p style={{ fontSize: '0.8rem', color: 'var(--mid-gray)' }}>
                                        Qty: {item.quantity}
                                        {item.size && ` · Size: ${item.size}`}
                                      </p>
                                    </div>
                                    <span style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--warm-brown)' }}>
                                      ${(parseFloat(item.unit_price) * item.quantity).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div style={{
                                marginTop: '1rem', textAlign: 'right',
                                borderTop: '1px solid var(--light-gray)', paddingTop: '0.8rem'
                              }}>
                                <span style={{ fontWeight: '700', fontSize: '1rem' }}>
                                  Order Total: ${parseFloat(order.total_amount).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* PROFILE TAB */}
            {activeTab === 'profile' && (
              <div style={{ maxWidth: '500px' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', marginBottom: '1.5rem' }}>
                  Personal Information
                </h3>

                {[
                  { label: 'Full Name', key: 'full_name', icon: User, type: 'text' },
                  { label: 'Phone Number', key: 'phone', icon: Phone, type: 'tel' },
                ].map(({ label, key, icon: Icon, type }) => (
                  <div key={key} style={{ marginBottom: '1.2rem' }}>
                    <label style={{ display: 'block', fontWeight: '500', fontSize: '0.88rem', marginBottom: '0.4rem' }}>
                      <Icon size={14} style={{ verticalAlign: 'middle', marginRight: '5px' }} />{label}
                    </label>
                    <input
                      type={type}
                      value={profile[key]}
                      onChange={e => setProfile({ ...profile, [key]: e.target.value })}
                      style={{
                        width: '100%', padding: '0.75rem 1rem',
                        border: '1.5px solid var(--light-gray)',
                        borderRadius: '10px', fontSize: '0.95rem',
                        outline: 'none', background: 'var(--cream)'
                      }}
                      onFocus={e => e.target.style.borderColor = 'var(--warm-brown)'}
                      onBlur={e => e.target.style.borderColor = 'var(--light-gray)'}
                    />
                  </div>
                ))}

                {/* Email (read-only) */}
                <div style={{ marginBottom: '1.2rem' }}>
                  <label style={{ display: 'block', fontWeight: '500', fontSize: '0.88rem', marginBottom: '0.4rem' }}>
                    <Mail size={14} style={{ verticalAlign: 'middle', marginRight: '5px' }} />Email
                  </label>
                  <input
                    type="email" value={user?.email} disabled
                    style={{
                      width: '100%', padding: '0.75rem 1rem',
                      border: '1.5px solid var(--light-gray)',
                      borderRadius: '10px', fontSize: '0.95rem',
                      background: 'var(--light-gray)', color: 'var(--mid-gray)', cursor: 'not-allowed'
                    }}
                  />
                </div>

                {/* Address */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '500', fontSize: '0.88rem', marginBottom: '0.4rem' }}>
                    <MapPin size={14} style={{ verticalAlign: 'middle', marginRight: '5px' }} />Delivery Address
                  </label>
                  <textarea
                    value={profile.address}
                    onChange={e => setProfile({ ...profile, address: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%', padding: '0.75rem 1rem',
                      border: '1.5px solid var(--light-gray)',
                      borderRadius: '10px', fontSize: '0.95rem',
                      outline: 'none', resize: 'vertical',
                      fontFamily: 'var(--font-body)', background: 'var(--cream)'
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--warm-brown)'}
                    onBlur={e => e.target.style.borderColor = 'var(--light-gray)'}
                  />
                </div>

                <button onClick={handleSaveProfile} disabled={saving} style={{
                  background: saving ? 'var(--mid-gray)' : 'var(--charcoal)',
                  color: 'var(--white)', border: 'none',
                  padding: '0.8rem 2rem', borderRadius: '10px',
                  fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer'
                }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}